// Module 5 — Claims Validation.
// Route: /reinsurance/claims
//
// Validate treaty attachment, documents and recoverables.
//
// The validation POSITION is a separate field from the claim's own
// lifecycle, and this screen filters on the former. A claim can be agreed
// and paid while its attachment is still unchecked; conflating the two is
// how a payment goes out against an unvalidated treaty.

import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ModulePage from './ModulePage';
import {
  C, Icons, Kpi, Pill, Panel, Row, Checklist, NextAction,
  PrimaryButton, GhostButton, TwoLine, ViewAllLink,
  money0, moneyMixed, date, humanise,
} from './kit';
import { EditPanel, StatusAction } from './actions';
import { Ring, ScoreBar } from './charts';
import { ActivityTab } from './tabs';

// The five validation checks, as the drawer shows them: the rule on the
// left, the verdict on the right, coloured by outcome. A checklist with no
// results reads as "all clear" when it means "never run", so an unvalidated
// claim says so in words.
function CheckList({ checks = [] }) {
  if (!checks.length) {
    return (
      <p style={{ margin: 0, fontSize: 12.5, color: C.muted, lineHeight: 1.55 }}>
        This claim has not been validated. Run the validation to check treaty
        attachment, limits, retention and exclusions.
      </p>
    );
  }
  const tone = { pass: C.green, warn: C.amber, fail: C.red };
  const verdict = { pass: 'Passed', warn: 'Review', fail: 'Failed' };
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 9 }}>
      {checks.map(c => (
        <li key={c.rule} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5 }}>
          <span style={{ color: tone[c.status] || C.muted, display: 'inline-flex', flexShrink: 0 }}>
            {c.status === 'pass' ? Icons.check() : Icons.alert()}
          </span>
          <span style={{ color: C.body, flex: 1, minWidth: 0 }} title={c.message}>
            {humanise(c.rule)}
          </span>
          <span style={{ color: tone[c.status] || C.muted, fontWeight: 600, flexShrink: 0 }}>
            {verdict[c.status] || humanise(c.status)}
          </span>
        </li>
      ))}
    </ul>
  );
}

// A claim notice hangs off a DEAL — the schema requires it, and without the
// link there is no treaty to validate the claim against. So the deal is
// picked from the real list rather than typed.
const claimForm = (t) => ({
  title: t('screens.reClaims.newTitle', 'New claim notice'),
  subtitle: t('screens.reClaims.newSub', 'Notify a loss against a placed programme'),
  submitLabel: t('screens.reClaims.createBtn', 'Notify claim'),
  endpoint: '/reinsurance/claims',
  fields: [
    { key: 'deal', label: t('screens.reClaims.fDealIn', 'Programme'), type: 'lookup', required: true,
      endpoint: '/reinsurance/deals',
      optionLabel: (d) => [d.dealNumber, d.cedantName || d.insuredName].filter(Boolean).join(' — '),
      placeholder: t('screens.reClaims.fDealPick', 'Choose the programme…'),
      emptyLabel: t('screens.reClaims.fDealNone', 'No programmes yet'),
      emptyHelp: t('screens.reClaims.fDealNoneHelp', 'A claim is validated against a placed programme — place one first.') },
    { key: 'claimReference', label: t('screens.reClaims.fRefIn', 'Cedant claim reference'),
      placeholder: 'GU-RE-CLM-2026-0007' },
    { key: 'dateOfLoss', label: t('screens.reClaims.fLossIn', 'Date of loss'), type: 'date', required: true },
    { key: 'dateReported', label: t('screens.reClaims.fReportedIn', 'Date reported'), type: 'date' },
    { key: 'causeOfLoss', label: t('screens.reClaims.fCauseIn', 'Cause of loss'), placeholder: 'Fire' },
    { key: 'claimedAmount', label: t('screens.reClaims.fGrossIn', 'Estimated gross loss'), type: 'number' },
    { key: 'reserveAmount', label: t('screens.reClaims.fReserveIn', 'Reserve'), type: 'number' },
    { key: 'currency', label: t('screens.reClaims.fCurrencyIn', 'Currency'), placeholder: 'USD' },
  ],
});

export default function ClaimsValidation() {
  const { t } = useTranslation();
  const history = useHistory();

  const columns = [
    { key: 'claimNumber', label: t('screens.reClaims.colRef', 'Claim ref'), width: 168,
      render: (r) => <span style={{ fontWeight: 600, color: C.ink }}>{r.claimNumber}</span> },
    { key: 'cedantName', label: t('screens.reClaims.colCedant', 'Cedant'),
      render: (r) => r.cedantName || '—' },
    { key: 'eventName', label: t('screens.reClaims.colEvent', 'Claimant / event'),
      render: (r) => <TwoLine primary={r.eventName || r.causeOfLoss} secondary={r.location} /> },
    { key: 'lineOfBusiness', label: t('screens.reClaims.colClass', 'Class'), width: 104,
      render: (r) => r.lineOfBusiness || '—' },
    { key: 'dateOfLoss', label: t('screens.reClaims.colLoss', 'Loss date'), width: 110,
      render: (r) => date(r.dateOfLoss) },
    { key: 'programType', label: t('screens.reClaims.colProgram', 'Program'), width: 104,
      render: (r) => (r.programType ? humanise(r.programType) : '—') },
    { key: 'claimedAmount', label: t('screens.reClaims.colGross', 'Est. gross loss'), numeric: true,
      render: (r) => money0(r.claimedAmount, r.currency) },
    { key: 'recoverable', label: t('screens.reClaims.colRecoverable', 'Est. recoverable'), numeric: true,
      // Null, not zero: an unvalidated claim has no recoverable figure yet,
      // and a 0 here would read as "nothing is recoverable".
      render: (r) => (r.recoverableSummary?.recoverable === null || r.recoverableSummary?.recoverable === undefined
        ? <span style={{ color: C.faint }}>not validated</span>
        : money0(r.recoverableSummary.recoverable, r.currency)) },
    { key: 'validationScore', label: t('screens.reClaims.colScore', 'Validation score'), width: 132,
      render: (r) => <ScoreBar score={r.validationScore} /> },
    { key: 'validationStatus', label: t('screens.reClaims.colStatus', 'Status'), width: 122,
      render: (r) => <Pill status={r.validationStatus || 'pending'} /> },
  ];

  return (
    <ModulePage
      title={t('screens.reClaims.title', 'Claims Validation')}
      subtitle={t('screens.reClaims.subtitle', 'Validate treaty attachment, documents and recoverables')}
      endpoint='/reinsurance/claims'
      searchPlaceholder={t('screens.reClaims.search', 'Search by claim ref, cedant, event or location…')}
      createForm={claimForm(t)}
      actions={({ openCreate }) => (
        <>
          {/* Both of these rendered as buttons and did nothing — no onClick
              at all. "New claim notice" now opens the create form; "Run
              validation" is a per-claim act, so it lives in the drawer where
              the claim is, rather than on a header with no claim selected. */}
          <GhostButton icon={Icons.plus()} onClick={openCreate}>
            {t('screens.reClaims.new', 'New claim notice')}
          </GhostButton>
        </>
      )}
      renderKpis={(s) => (
        <>
          <Kpi icon={Icons.doc()} tone='blue'
            value={s?.openClaimReviews?.value ?? '—'} trend={s?.openClaimReviews?.trend}
            label={t('screens.reClaims.kpiOpen', 'Open claim reviews')} />
          <Kpi icon={Icons.clip()} tone='amber'
            value={s?.claimsPendingDocuments?.value ?? '—'} trend={s?.claimsPendingDocuments?.trend}
            label={t('screens.reClaims.kpiDocs', 'Claims pending documents')} />
          <Kpi icon={Icons.coins()} tone='green'
            value={moneyMixed(s?.estimatedRecoverables)} trend={s?.estimatedRecoverables?.trend}
            comparison={s?.estimatedRecoverables?.comparison}
            label={t('screens.reClaims.kpiRecoverable', 'Estimated recoverables')} />
          <Kpi icon={Icons.alert()} tone='red'
            value={s?.urgentSettlements?.value ?? '—'} trend={s?.urgentSettlements?.trend}
            label={t('screens.reClaims.kpiUrgent', 'Urgent settlements')} />
        </>
      )}
      filters={[
        { key: 'period', type: 'daterange', label: 'Loss date' },
        { key: 'validationStatus', label: 'Status', options: [
          { value: 'all', label: 'All statuses' },
          { value: 'pending', label: 'Pending' },
          { value: 'in_review', label: 'In Review' },
          { value: 'pending_docs', label: 'Pending Docs' },
          { value: 'more_info', label: 'More Info' },
          { value: 'validated', label: 'Validated' },
          { value: 'rejected', label: 'Rejected' },
        ] },
        { key: 'programType', label: 'Program', options: [
          { value: 'all', label: 'All programs' },
          { value: 'treaty', label: 'Treaty' },
          { value: 'facultative', label: 'Facultative' },
        ] },
      ]}
      columns={columns}
      emptyMessage={t('screens.reClaims.empty', 'No reinsurance claims. Notify one against a placed deal to validate its recoverable.')}
      drawerTabs={['Overview', 'Coverage Test', 'Documents', 'Activity']}
      drawerHeader={(r) => ({
        title: r.claimNumber,
        badge: <Pill status={r.validationStatus || 'pending'} />,
        lines: [r.eventName, r.cedantName].filter(Boolean),
      })}
      drawer={(r, { tab, setTab, reload }) => {
        const docs = r.requiredDocuments || [];
        const done = docs.filter(d => d.uploaded).length;
        const sum = r.recoverableSummary || {};

        if (tab === 'Coverage Test') {
          return (
            <>
              <Panel title={t('screens.reClaims.checklist', 'Validation checklist')}>
                <CheckList checks={r.validation?.checks || []} />
              </Panel>
              <Panel title={t('screens.reClaims.reserve', 'Reserve & recoverable summary')}>
                <Row label={t('screens.reClaims.fGross', 'Estimated gross loss')} value={money0(sum.gross, r.currency)} />
                <Row label={t('screens.reClaims.fRetention', 'Applicable retention')} value={sum.retention === null ? null : money0(sum.retention, r.currency)} />
                <Row label={t('screens.reClaims.fNet', 'Net loss (post retention)')} value={sum.netLoss === null ? null : money0(sum.netLoss, r.currency)} />
                <Row label={t('screens.reClaims.fRecoverable', 'Estimated recoverable')} value={sum.recoverable === null ? null : money0(sum.recoverable, r.currency)} strong />
                <Row label={t('screens.reClaims.fPct', 'Recoverable %')} value={sum.recoverablePct === null || sum.recoverablePct === undefined ? null : `${Math.round(sum.recoverablePct)}%`} />
              </Panel>
            </>
          );
        }

        if (tab === 'Documents') {
          return (
            <Panel title={t('screens.reClaims.reqDocs', 'Required claim documents')}>
              <Checklist items={docs} countLabel={`${done} of ${docs.length}`} />
            </Panel>
          );
        }

        // Activity had no branch, so selecting it fell through to the Overview
        // return below and rendered the Overview again — the same failure the
        // seven-tab fix of 4683c1f9 cleared everywhere else. It survived here
        // because a tab that renders plausible content does not look broken;
        // QA caught it by comparing the two tabs side by side.
        if (tab === 'Activity') return <ActivityTab resource='claims' id={r._id} />;

        return (
          <>
            <Panel title={t('screens.reClaims.info', 'Claim Information')}>
              <Row label={t('screens.reClaims.fRef', 'Claim ref')} value={r.claimNumber} />
              <Row label={t('screens.reClaims.fCedant', 'Cedant')} value={r.cedantName} />
              <Row label={t('screens.reClaims.fClass', 'Class')} value={r.lineOfBusiness} />
              <Row label={t('screens.reClaims.fLoss', 'Loss date')} value={date(r.dateOfLoss)} />
              <Row label={t('screens.reClaims.fEvent', 'Event')} value={r.eventName} />
              <Row label={t('screens.reClaims.fLocation', 'Location')} value={r.location} />
              <Row label={t('screens.reClaims.fProgram', 'Program')} value={r.programName || (r.programType ? humanise(r.programType) : null)} />
              <Row label={t('screens.reClaims.fReported', 'Reported gross loss')} value={money0(r.claimedAmount, r.currency)} />
              <Row label={t('screens.reClaims.fRecoverable', 'Estimated recoverable')} value={sum.recoverable === null ? null : money0(sum.recoverable, r.currency)} />
              <Row label={t('screens.reClaims.fScore', 'Validation score')} value={<ScoreBar score={r.validationScore} />} />
              <Row label={t('screens.reClaims.fUpdated', 'Last updated')} value={date(r.updatedAt)} />
            </Panel>

            {/* A claim could be created and then never touched: the drawer
                offered no edit and no way to move the validation state, which
                is what QA logged as "EDIT OPTION IS NOT PRESENT" and "STATUS
                TRANSITIONS: NOT PRESENT". Same primitives the recovery and
                account drawers use, against the claim's own PATCH — which
                allows these fields (updateClaim denies only the identity and
                the validation evidence). `validationStatus`, not `status`:
                the claim's state lives on its own field. */}
            <Panel title={t('screens.reClaims.record', 'Record')}>
              <div style={{ display: 'grid', gap: 10 }}>
                <EditPanel
                  record={r}
                  endpoint='/reinsurance/claims'
                  onSaved={reload}
                  fields={[
                    { key: 'eventName', label: t('screens.reClaims.fEvent', 'Event') },
                    { key: 'location', label: t('screens.reClaims.fLocation', 'Location') },
                    { key: 'dateOfLoss', label: t('screens.reClaims.fLoss', 'Loss date'), type: 'date' },
                    { key: 'claimedAmount', label: t('screens.reClaims.fReported', 'Reported gross loss'), type: 'number' },
                    { key: 'reserveAmount', label: t('screens.reClaims.fReserve', 'Reserve'), type: 'number' },
                    { key: 'causeOfLoss', label: t('screens.reClaims.fCause', 'Cause of loss') },
                  ]}
                />
                <StatusAction
                  record={r}
                  endpoint='/reinsurance/claims'
                  field='validationStatus'
                  label={t('screens.reClaims.changeState', 'Change validation state')}
                  onSaved={reload}
                  options={[
                    { value: 'pending', label: 'Pending' },
                    { value: 'in_review', label: 'In Review' },
                    { value: 'pending_docs', label: 'Pending Docs' },
                    { value: 'more_info', label: 'More Info' },
                    { value: 'validated', label: 'Validated' },
                    { value: 'rejected', label: 'Rejected' },
                  ]}
                />
              </div>
            </Panel>

            {/* The two links below carried no onClick: cursor-pointer buttons
                with an arrow that did nothing. Each now opens the tab it is
                promising — the full checklist lives on Coverage Test, the
                documents on Documents. */}
            <Panel
              title={t('screens.reClaims.checklist', 'Validation checklist')}
              action={<ViewAllLink onClick={() => setTab('Coverage Test')}>{t('screens.reClaims.viewDetails', 'View details')}</ViewAllLink>}
            >
              <CheckList checks={r.validation?.checks || []} />
            </Panel>

            <Panel
              title={t('screens.reClaims.reqDocs', 'Required claim documents')}
              action={<ViewAllLink onClick={() => setTab('Documents')}>{t('common.viewAll', 'View all')}</ViewAllLink>}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 13, marginBottom: 12 }}>
                <Ring done={done} total={docs.length} />
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>
                    {t('screens.reClaims.docsOf', '{{done}} of {{total}}', { done, total: docs.length })}
                  </div>
                  <div style={{ fontSize: 12, color: C.muted }}>
                    {t('screens.reClaims.docsUploaded', 'documents uploaded')}
                  </div>
                </div>
              </div>
              <Checklist items={docs} />
            </Panel>

            <Panel title={t('screens.reClaims.reserve', 'Reserve & recoverable summary')}>
              <Row label={t('screens.reClaims.fGross', 'Estimated gross loss')} value={money0(sum.gross, r.currency)} />
              <Row label={t('screens.reClaims.fRetention', 'Applicable retention')} value={sum.retention === null ? null : money0(sum.retention, r.currency)} />
              <Row label={t('screens.reClaims.fNet', 'Net loss (post retention)')} value={sum.netLoss === null ? null : money0(sum.netLoss, r.currency)} />
              <Row label={t('screens.reClaims.fRecoverable', 'Estimated recoverable')} value={sum.recoverable === null ? null : money0(sum.recoverable, r.currency)} strong />
              <Row label={t('screens.reClaims.fPct', 'Recoverable %')} value={sum.recoverablePct === null || sum.recoverablePct === undefined ? null : `${Math.round(sum.recoverablePct)}%`} />
            </Panel>

            {/* The drawer reads the record; running validation, generating
                cedant advice and raising reinsurer debits happen on the
                workspace. One record, one read view, one place to act on
                it - not two copies of the same read view. */}
            <NextAction
              label={r.nextAction?.label || t('screens.reClaims.openWorkspace', 'Open the validation workspace')}
              detail={r.nextAction?.detail || t('screens.reClaims.workspaceDetail', 'Run validation, generate cedant advice and raise reinsurer debits.')}
              button={t('screens.reClaims.validate', 'Run validation')}
              onButton={() => history.push(`/reinsurance/claims/${r._id}`)}
            />
          </>
        );
      }}
    />
  );
}
